/**
 * Configuration defaults: brief Section 17 as carried by the engine
 * config types, amended by owner decisions S4 (estimate policy),
 * S6 (standard proportion disclosure on), D-4 (2026-10-17: benchmarking
 * on by default, superseding S5's off-by-default after study E-3),
 * RB-3 (2026-10-17: the default run is Full mode at B = 200), and
 * Addendum 22 (2026-10-18, study RB-6 under the Addendum 21 owner
 * criteria): the default run is now Quick mode with benchmarking,
 * covariate selection on, and B = 200 (the bootstrap serves the
 * adjusted-REML cells, per C-1). No candidate met all of the
 * Addendum 21 gates at flagship scale; this configuration is the
 * tie-break-top candidate (highest flagship n < 20 coverage) and
 * ships under the recorded owner exception in Addendum 22, with
 * its measured coverage stated in the methods report and the
 * exported-table footnote. Full mode remains selectable. This
 * supersedes brief AUT-02's Full-for-results default by owner
 * decision, on the RB-6 measurements.
 */

import type { DisclosureConfig, TransformMethod } from '@sae/engine';

import type {
  DisclosurePresetId,
  ProjectState,
  RunConfigState,
} from './types.js';

export function defaultRunConfig(): RunConfigState {
  return {
    mode: 'quick',
    transform: 'auto',
    benchmarkingEnabled: true,
    benchmarkingMethod: 'logit',
    transformBiasCorrection: false,
    deffBasis: 'statewide',
    disclosurePresetId: 'standard',
    bootstrapB: 200,
    seed: 'sae-studio',
    confidence: 0.95,
  };
}

/**
 * Map the Step 2 preset row onto an engine DisclosureConfig.
 *
 * - 'standard' is the engine's proportion-standard preset (decision S6).
 * - 'strict' keeps the CV ladder with the flag rung lowered to 20%
 *   (the mockup's label); the structural rules are specific to
 *   the proportion-standard preset, so strict runs on 'custom'.
 * - 'off' sets thresholds no finite CV or count can reach. A large
 *   finite sentinel (not Infinity) keeps the config JSON-safe for
 *   autosave.
 * - 'sae-standard' is the engine's model-based preset "SAE
 *   Standard" (Path to A guide, Part 4.2). Its thresholds are the
 *   preset's own structural constants, so the ladder fields below
 *   are carried for config completeness and are not read by it.
 *   It is selectable here; the default stays 'standard' — making
 *   SAE Standard the default is gated on R-4 (OD-3).
 */
export function disclosureConfigFor(
  presetId: DisclosurePresetId,
): DisclosureConfig {
  switch (presetId) {
    case 'standard':
      return {
        preset: 'proportion-standard',
        minRespondents: 10,
        cvFlag: 0.3,
        cvSuppress: 0.5,
        minPsusPerArea: 1,
      };
    case 'strict':
      return {
        preset: 'custom',
        minRespondents: 10,
        cvFlag: 0.2,
        cvSuppress: 0.5,
        minPsusPerArea: 1,
      };
    case 'off':
      return {
        preset: 'custom',
        minRespondents: 0,
        cvFlag: 1e9,
        cvSuppress: 1e9,
        minPsusPerArea: 1,
      };
    case 'sae-standard':
      return {
        preset: 'sae-standard',
        minRespondents: 10,
        cvFlag: 0.3,
        cvSuppress: 0.5,
        minPsusPerArea: 1,
      };
  }
}

/**
 * Resolve the declared transform (S4: chosen automatically by
 * outcome type and always declared). Every outcome the MVP handles
 * is a proportion, so 'auto' resolves to the arcsine transform of
 * brief 9.5; an expert override is honored as chosen.
 */
export function resolveTransform(
  choice: RunConfigState['transform'],
): TransformMethod {
  return choice === 'auto' ? 'arcsin' : choice;
}

export function initialProjectState(): ProjectState {
  return {
    version: 1,
    projectName: 'New project',
    step: 'upload',
    furthestStep: 'upload',
    source: {
      kind: 'none',
      filename: null,
      format: null,
      table: null,
      tableMissing: false,
      readIssues: [],
    },
    covariatesSource: null,
    setup: null,
    config: defaultRunConfig(),
    qc: null,
    feasibility: null,
    run: {
      status: 'idle',
      progress: [],
      result: null,
      stale: false,
      error: null,
      startedAt: null,
      finishedAt: null,
    },
    license: {
      status: 'trial',
      email: null,
      verifiedAt: null,
      expiresAt: null,
      lastError: null,
    },
    selectedCellId: null,
    keepDataWithProject: true,
    revision: 0,
  };
}
